import { ObservationSource } from '@jkhy/frontend-utils/observable';
import { TmBaseComponent } from '@treasury/presentation/tm-base.component';
import '@treasury/presentation/components/tm-loader';
import { css, html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { tagName } from './back-office-app.types';

/**
 * The root element of Back Office V2.
 * It primarily exists as an outlet to render router content.
 */
@customElement(tagName)
export class BackOfficeApp extends TmBaseComponent {
    public static override readonly styles = css`
        :host {
            display: block;
            min-height: 100vh;
        }

        .loading-overlay {
            position: fixed;
            inset: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: var(--bov2-surface, #ededed);
            z-index: 1000;
        }
    `;

    /**
     * Observable that emits any time the outlet element becomes connected to the DOM.
     */
    private static outletSource = new ObservationSource<Element>();

    public static async getOutlet() {
        return BackOfficeApp.outletSource.toPromise(true);
    }

    /**
     * Shows a full-screen loading indicator until the router resolves the first route. The initial
     * route runs through the auth guard (authentication, entitlements, and route checks), during
     * which the outlet is empty — without this the user would see a blank white screen.
     */
    @state()
    private initialLoading = true;

    public connectedCallback() {
        super.connectedCallback();
        window.addEventListener('vaadin-router-location-changed', this.onFirstRouteResolved);
        window.addEventListener('vaadin-router-error', this.onFirstRouteResolved);
    }

    public disconnectedCallback() {
        super.disconnectedCallback();
        window.removeEventListener('vaadin-router-location-changed', this.onFirstRouteResolved);
        window.removeEventListener('vaadin-router-error', this.onFirstRouteResolved);
    }

    public render() {
        return html`
            ${this.initialLoading
                ? html`<div class="loading-overlay" role="status" aria-label="Loading">
                      <tm-loader></tm-loader>
                  </div>`
                : nothing}
            <main ${ref(element => this.outletChanged(element))}></main>
        `;
    }

    private readonly onFirstRouteResolved = () => {
        this.initialLoading = false;
        window.removeEventListener('vaadin-router-location-changed', this.onFirstRouteResolved);
        window.removeEventListener('vaadin-router-error', this.onFirstRouteResolved);
    };

    private outletChanged(element?: Element) {
        if (!element) {
            return;
        }

        BackOfficeApp.outletSource.emit(element);
    }
}
